<main class="purchase-orders-page">
  <header class="page-header">
    @if (id) {
      <a class="back-link" routerLink="/purchase-orders"
        ><mat-icon aria-hidden="true">arrow_back</mat-icon
        >{{ t.getPurchaseOrderBackText() }}</a
      >
    }
    <div class="heading-row">
      <div>
        <h1>
          {{ id ? t.getPurchaseOrderTitleText() : t.getPurchaseOrdersText() }}
        </h1>
        @if (order) {
          <p class="reference">{{ displayNumber(order) }}</p>
          <span
            class="status-chip detail-status"
            [attr.data-status]="order.status"
          >
            <mat-icon aria-hidden="true">{{
              statusIcon(order.status)
            }}</mat-icon>
            {{ t.getRequisitionStatusText(order.status) }}
          </span>
        }
      </div>
      @if (order) {
        <button
          mat-stroked-button
          type="button"
          [disabled]="downloading"
          (click)="download()"
        >
          <mat-icon aria-hidden="true">download</mat-icon
          >{{
            downloading
              ? t.getPurchaseOrderDownloadingText()
              : t.getPurchaseOrderDownloadText()
          }}
        </button>
      }
    </div>
  </header>
  @if (loading) {
    <div class="state" role="status">
      <mat-spinner diameter="32"></mat-spinner
      >{{ t.getPurchaseOrderLoadingText() }}
    </div>
  } @else if (error) {
    <div class="state">
      <p class="error" role="alert">{{ error }}</p>
      <button mat-stroked-button (click)="load()">
        {{ t.getMarketplaceRetryText() }}
      </button>
    </div>
  } @else if (order) {
    @if (downloadError) {
      <div class="download-error">
        <p class="error" role="alert">{{ downloadError }}</p>
        <button
          mat-stroked-button
          (click)="download()"
          [disabled]="downloading"
        >
          {{ t.getMarketplaceRetryText() }}
        </button>
      </div>
    }
    <section class="card totals">
      <div class="totals-heading">
        <h2>{{ t.getRequisitionKnownSubtotalText() }}</h2>
        <p class="muted order-counts">
          {{ t.getRequisitionItemCountText(order.items.length) }} ·
          {{ t.getPurchaseOrderSupplierCountText(groups.length) }}
        </p>
      </div>
      @if (order.knownSubtotals.length) {
        <dl class="currency-subtotals">
          @for (total of order.knownSubtotals; track total.currency) {
            <div>
              <dt>{{ total.currency }}</dt>
              <dd>
                {{ formatAmount(total.amount, subtotalScale(total.currency)) }}
              </dd>
            </div>
          }
        </dl>
      } @else {
        <p>{{ t.getPurchaseOrderNoPricesText() }}</p>
      }
      @if (order.unpricedItemCount) {
        <p class="cost-note">
          {{ t.getRequisitionUnpricedText(order.unpricedItemCount) }}.
          {{ t.getPurchaseOrderIncompleteCostText() }}
        </p>
      }
      <p class="muted cost-note">{{ t.getPurchaseOrderCurrenciesText() }}</p>
    </section>
    <section class="card">
      <h2>{{ t.getPurchaseOrderBuyerText() }}</h2>
      <dl class="details-grid">
        <div>
          <dt>{{ t.getPurchaseOrderBuyerText() }}</dt>
          <dd>{{ order.buyer.name }}</dd>
        </div>
        @if (order.buyer.email) {
          <div>
            <dt>{{ t.getEmailAddressText() }}</dt>
            <dd>{{ order.buyer.email }}</dd>
          </div>
        }
        @if (order.buyer.phone) {
          <div>
            <dt>{{ t.getPurchaseOrderPhoneText() }}</dt>
            <dd>{{ order.buyer.phone }}</dd>
          </div>
        }
        <div>
          <dt>{{ t.getRequisitionRecipientText() }}</dt>
          <dd>{{ order.recipient }}</dd>
        </div>
        @if (order.deliveryAddress) {
          <div>
            <dt>{{ t.getRequisitionDeliveryText() }}</dt>
            <dd>{{ order.deliveryAddress }}</dd>
          </div>
        }
        @if (order.collectionLocation) {
          <div>
            <dt>{{ t.getRequisitionCollectionText() }}</dt>
            <dd>{{ order.collectionLocation }}</dd>
          </div>
        }
        @if (order.neededBy) {
          <div>
            <dt>{{ t.getRequisitionNeededByText() }}</dt>
            <dd>{{ order.neededBy | date: 'mediumDate' }}</dd>
          </div>
        }
        @if (order.note) {
          <div class="full-width">
            <dt>{{ t.getRequisitionNoteText() }}</dt>
            <dd>{{ order.note }}</dd>
          </div>
        }
      </dl>
      <a [routerLink]="['/requisitions', order.requisitionId]"
        >{{ t.getPurchaseOrderSourceText() }}:
        {{ order.requisitionReference }}</a
      >
      <details class="record-details">
        <summary>{{ t.getPurchaseOrderRecordDetailsText() }}</summary>
        <dl class="details-grid">
          <div>
            <dt>{{ t.getPurchaseOrderNumberText() }}</dt>
            <dd>{{ order.number }}</dd>
          </div>
        </dl>
      </details>
    </section>
    <section class="card">
      <h2>{{ t.getPurchaseOrderReviewDetailsText() }}</h2>
      <dl class="details-grid">
        <div>
          <dt>{{ t.getPurchaseOrderRequestedByText() }}</dt>
          <dd>
            {{ order.requesterName || t.getPurchaseOrderNotAvailableText() }}
          </dd>
        </div>
        <div>
          <dt>{{ t.getRequisitionSubmittedAtText() }}</dt>
          <dd>
            @if (order.submittedAt) {
              <time [attr.datetime]="order.submittedAt">{{
                order.submittedAt | date: 'medium'
              }}</time>
            } @else {
              {{ t.getPurchaseOrderNotAvailableText() }}
            }
          </dd>
        </div>
        <div>
          <dt>{{ t.getPurchaseOrderApproverText() }}</dt>
          <dd>
            {{
              order.reviewerName ||
                order.reviewerEmail ||
                t.getPurchaseOrderNotAvailableText()
            }}
            @if (order.reviewerName && order.reviewerEmail) {
              <span class="participant-email muted">{{
                order.reviewerEmail
              }}</span>
            }
          </dd>
        </div>
        <div>
          <dt>{{ t.getPurchaseOrderDecisionAtText() }}</dt>
          <dd>
            @if (order.reviewedAt) {
              <time [attr.datetime]="order.reviewedAt">{{
                order.reviewedAt | date: 'medium'
              }}</time>
            } @else {
              {{ t.getPurchaseOrderNotAvailableText() }}
            }
          </dd>
        </div>
      </dl>
    </section>
    @for (group of groups; track group.id) {
      <section class="card supplier-group">
        <h2>{{ t.getPurchaseOrderSupplierText() }}: {{ group.name }}</h2>
        @for (item of group.items; track item.productId) {
          <article class="item">
            <h3>{{ item.productName }}</h3>
            @if (item.brand) {
              <p class="muted">{{ item.brand }}</p>
            }
            <dl class="details-grid">
              <div>
                <dt>{{ t.getRequisitionApprovedQuantityText() }}</dt>
                <dd>
                  {{ item.quantity }}
                  {{ t.getMarketplaceRequestUnitValueText(item.unitOfMeasure) }}
                </dd>
              </div>
              <div>
                <dt>{{ t.getRequisitionUnitPriceText() }}</dt>
                <dd>
                  @if (item.lineAmount === null) {
                    {{ t.getPurchaseOrderUnpricedText() }}
                  } @else {
                    {{ formatAmount(item.priceAmount!) }}
                    {{ item.currency }}
                  }
                </dd>
              </div>
              @if (item.lineAmount !== null) {
                <div>
                  <dt>{{ t.getPurchaseOrderLineAmountText() }}</dt>
                  <dd>
                    {{ formatAmount(item.lineAmount, lineAmountScale(item)) }}
                    {{ item.currency }}
                  </dd>
                </div>
              }
            </dl>
            @if (references(item).length) {
              <details>
                <summary>{{ t.getPurchaseOrderReferencesText() }}</summary>
                <dl class="details-grid references">
                  @for (ref of references(item); track ref.label) {
                    <div>
                      <dt>{{ ref.label }}</dt>
                      <dd>{{ ref.value }}</dd>
                    </div>
                  }
                </dl>
              </details>
            }
          </article>
        }
      </section>
    }
    <div class="document-actions">
      <button
        mat-stroked-button
        type="button"
        [disabled]="downloading"
        (click)="download()"
      >
        <mat-icon aria-hidden="true">download</mat-icon>
        {{
          downloading
            ? t.getPurchaseOrderDownloadingText()
            : t.getPurchaseOrderDownloadText()
        }}
      </button>
    </div>
  } @else if (!orders.length) {
    <div class="state empty-state">
      <span class="empty-icon"
        ><mat-icon aria-hidden="true">receipt_long</mat-icon></span
      >
      <p>{{ t.getPurchaseOrderEmptyText() }}</p>
      <p class="muted">{{ t.getPurchaseOrderEmptyHintText() }}</p>
      <a mat-stroked-button routerLink="/requisitions">{{
        t.getRequisitionSavedText()
      }}</a>
    </div>
  } @else {
    <div class="list-filters">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>{{ t.getRequisitionStatusFilterText() }}</mat-label>
        <mat-icon matPrefix aria-hidden="true">filter_list</mat-icon>
        <mat-select
          [(ngModel)]="statusFilter"
          (selectionChange)="filterChanged()"
        >
          <mat-option value="all">{{
            t.getRequisitionAllStatusesText()
          }}</mat-option>
          @for (status of statusFilters; track status) {
            <mat-option [value]="status">{{
              t.getRequisitionStatusText(status)
            }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (statusFilter !== 'all') {
        <button mat-button (click)="clearFilter()">
          <mat-icon aria-hidden="true">close</mat-icon>
          {{ t.getRequisitionClearFilterText() }}
        </button>
      }
    </div>
    @if (!filteredOrders.length) {
      <p class="filter-empty" role="status">
        {{ t.getPurchaseOrderNoFilterMatchesText() }}
      </p>
    }
    <div class="saved-list">
      @for (entry of visibleOrders; track entry.id) {
        <a class="saved-card" [routerLink]="['/purchase-orders', entry.id]">
          <span class="saved-card-icon">
            <mat-icon aria-hidden="true">receipt_long</mat-icon>
          </span>
          <span class="saved-card-content">
            <span class="saved-card-top">
              <span class="saved-reference">{{ displayNumber(entry) }}</span>
              <span class="status-chip" [attr.data-status]="entry.status">
                <mat-icon aria-hidden="true">{{
                  statusIcon(entry.status)
                }}</mat-icon>
                {{ t.getRequisitionStatusText(entry.status) }}
              </span>
            </span>
            <strong class="saved-recipient">{{ entry.recipient }}</strong>
            <span class="saved-card-meta">
              <span>{{
                t.getRequisitionItemCountText(entry.items.length)
              }}</span>
              <span class="meta-divider" aria-hidden="true"></span>
              <span>{{ entry.createdAt | date: 'mediumDate' }}</span>
              <span class="meta-divider" aria-hidden="true"></span>
              <span>{{ entry.requisitionReference }}</span>
            </span>
          </span>
          <mat-icon class="saved-card-arrow" aria-hidden="true"
            >chevron_right</mat-icon
          >
        </a>
      }
    </div>
    @if (filteredOrders.length) {
      <mat-paginator
        [length]="filteredOrders.length"
        [pageSize]="pageSize"
        [pageIndex]="pageIndex"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="pageChanged($event)"
        [attr.aria-label]="t.getPurchaseOrdersText()"
      ></mat-paginator>
    }
  }
</main>
